(() => {
  'use strict';

  const ANALYTICS_SCOPE = 'https://www.googleapis.com/auth/analytics.readonly';
  const DATA_API = 'https://analyticsdata.googleapis.com/v1alpha/properties';
  const ADMIN_API = 'https://analyticsadmin.googleapis.com/v1beta/accountSummaries';

  const stringFilter = (fieldName, matchType, value) => ({
    filter: {
      fieldName,
      stringFilter: { matchType, value, caseSensitive: false }
    }
  });

  const funnelFieldFilter = (fieldName, matchType, value) => ({
    funnelFieldFilter: {
      fieldName,
      stringFilter: { matchType, value, caseSensitive: false }
    }
  });

  const eventFilter = (eventName) => ({ funnelEventFilter: { eventName } });

  const landingFilter = funnelFieldFilter('landingPagePlusQueryString', 'CONTAINS', '/');
  const productViewFilter = {
    orGroup: {
      expressions: [
        eventFilter('view_item'),
        funnelFieldFilter('pagePathPlusQueryString', 'CONTAINS', 'products')
      ]
    }
  };

  const transitions = [
    {
      key: 'landing_to_product',
      label: 'Landing → Product View',
      shortLabel: 'Landing → Product',
      fromName: 'Landing',
      toName: 'Product View',
      fromFilter: landingFilter,
      toFilter: productViewFilter
    },
    {
      key: 'product_to_cart',
      label: 'Product View → Add to Cart',
      shortLabel: 'Product → Cart',
      fromName: 'Product View',
      toName: 'Add to Cart',
      fromFilter: productViewFilter,
      toFilter: eventFilter('add_to_cart')
    },
    {
      key: 'cart_to_checkout',
      label: 'Add to Cart → Begin Checkout',
      shortLabel: 'Cart → Checkout',
      fromName: 'Add to Cart',
      toName: 'Begin Checkout',
      fromFilter: eventFilter('add_to_cart'),
      toFilter: eventFilter('begin_checkout')
    },
    {
      key: 'checkout_to_purchase',
      label: 'Begin Checkout → Purchase',
      shortLabel: 'Checkout → Purchase',
      fromName: 'Begin Checkout',
      toName: 'Purchase',
      fromFilter: eventFilter('begin_checkout'),
      toFilter: eventFilter('purchase')
    }
  ];

  // Landing page buckets match on the first path segment so a session can only fall in
  // one of them; otherwise "% Users" double counts paths like /pages/product-care.
  const homepageLandingFilter = stringFilter('landingPage', 'EXACT', '/');
  const landingPathFilter = (path) => stringFilter('landingPage', 'FULL_REGEXP', `/${path}(/.*)?`);

  const namedLandingFilters = [
    homepageLandingFilter,
    landingPathFilter('products'),
    landingPathFilter('collections'),
    landingPathFilter('pages'),
    landingPathFilter('blogs')
  ];

  const otherLandingFilter = {
    notExpression: {
      orGroup: {
        expressions: namedLandingFilters
      }
    }
  };

  const smsChannelFilter = stringFilter('sessionDefaultChannelGrouping', 'EXACT', 'SMS');
  const emailChannelFilter = stringFilter('sessionDefaultChannelGrouping', 'EXACT', 'Email');
  const directChannelFilter = stringFilter('sessionDefaultChannelGrouping', 'EXACT', 'Direct');
  const organicChannelFilter = stringFilter('sessionDefaultChannelGrouping', 'CONTAINS', 'Organic');
  const paidChannelFilter = stringFilter(
    'sessionDefaultChannelGrouping',
    'FULL_REGEXP',
    '^(Paid (Search|Social|Shopping|Video|Other)|Display|Cross-network)$'
  );

  // Catches the groupings without a row of their own (Referral, Affiliates, Unassigned,
  // Audio, Push) so the channel table adds up to the whole audience.
  const otherChannelFilter = {
    notExpression: {
      orGroup: {
        expressions: [
          smsChannelFilter,
          emailChannelFilter,
          directChannelFilter,
          organicChannelFilter,
          paidChannelFilter
        ]
      }
    }
  };

  const allSegments = [
    { id: 'mobile', label: 'Mobile', breakdownKey: 'device', filter: stringFilter('deviceCategory', 'EXACT', 'mobile') },
    { id: 'desktop', label: 'Desktop', breakdownKey: 'device', filter: stringFilter('deviceCategory', 'EXACT', 'desktop') },
    { id: 'tablet', label: 'Tablet', breakdownKey: 'device', filter: stringFilter('deviceCategory', 'EXACT', 'tablet') },
    { id: 'sms', label: 'SMS', breakdownKey: 'channel', filter: smsChannelFilter },
    { id: 'email', label: 'Email', breakdownKey: 'channel', filter: emailChannelFilter },
    { id: 'direct', label: 'Direct', breakdownKey: 'channel', filter: directChannelFilter },
    { id: 'organic', label: 'Organic', breakdownKey: 'channel', filter: organicChannelFilter },
    { id: 'paid', label: 'Paid', breakdownKey: 'channel', filter: paidChannelFilter },
    { id: 'other-channel', label: 'Other channels', breakdownKey: 'channel', filter: otherChannelFilter },
    { id: 'homepage', label: 'Homepage', breakdownKey: 'landingPage', filter: homepageLandingFilter },
    { id: 'products', label: '/products/', breakdownKey: 'landingPage', filter: landingPathFilter('products') },
    { id: 'collections', label: '/collections/', breakdownKey: 'landingPage', filter: landingPathFilter('collections') },
    { id: 'pages', label: '/pages/', breakdownKey: 'landingPage', filter: landingPathFilter('pages') },
    { id: 'blogs', label: '/blogs/', breakdownKey: 'landingPage', filter: landingPathFilter('blogs') },
    { id: 'other', label: 'Other landing pages', breakdownKey: 'landingPage', filter: otherLandingFilter },
    { id: 'new', label: 'New', breakdownKey: 'userType', filter: stringFilter('newVsReturning', 'EXACT', 'new') },
    { id: 'returning', label: 'Returning', breakdownKey: 'userType', filter: stringFilter('newVsReturning', 'EXACT', 'returning') },
    { id: 'us', label: 'US', breakdownKey: 'country', filter: stringFilter('country', 'EXACT', 'United States') },
    { id: 'non-us', label: 'Non-US', breakdownKey: 'country', filter: { notExpression: stringFilter('country', 'EXACT', 'United States') } }
  ];

  const breakdownDefinitions = [
    { key: 'device', label: 'By device' },
    { key: 'channel', label: 'By channel' },
    { key: 'landingPage', label: 'By landing page type' },
    { key: 'userType', label: 'By user type' },
    { key: 'country', label: 'By country' }
  ];

  const demoRows = {
    device: [
      ['mobile', 68, [0.58, 0.131, 0.47, 0.52]],
      ['desktop', 27, [0.66, 0.192, 0.58, 0.63]],
      ['tablet', 5, [0.6, 0.16, 0.5, 0.55]]
    ],
    channel: [
      ['sms', 4, [0.71, 0.22, 0.6, 0.66]],
      ['email', 12, [0.65, 0.19, 0.57, 0.61]],
      ['direct', 18, [0.6, 0.16, 0.52, 0.58]],
      ['organic', 33, [0.59, 0.15, 0.5, 0.55]],
      ['paid', 28, [0.55, 0.098, 0.46, 0.5]],
      ['other-channel', 5, [0.52, 0.12, 0.45, 0.49]]
    ],
    landingPage: [
      ['homepage', 30, [0.52, 0.14, 0.49, 0.55]],
      ['products', 28, [0.74, 0.18, 0.55, 0.6]],
      ['collections', 30, [0.63, 0.15, 0.5, 0.54]],
      ['pages', 7, [0.48, 0.11, 0.44, 0.49]],
      ['blogs', 5, [0.46, 0.1, 0.42, 0.47]],
      ['other', 12, [0.4, 0.11, 0.44, 0.5]]
    ],
    userType: [
      ['new', 62, [0.56, 0.13, 0.48, 0.52]],
      ['returning', 38, [0.7, 0.2, 0.59, 0.64]]
    ],
    country: [
      ['us', 72, [0.63, 0.16, 0.53, 0.58]],
      ['non-us', 28, [0.57, 0.14, 0.49, 0.54]]
    ]
  };

  const REPORT_CACHE_TTL = 30 * 60 * 1000;

  const formatPercent = (rate, digits = 1) => rate === null ? '—' : `${(rate * 100).toFixed(digits)}%`;
  const formatSigned = (value) => value === null ? '—' : `${value >= 0 ? '+' : ''}${value}%`;
  const delay = (milliseconds) => new Promise((resolve) => window.setTimeout(resolve, milliseconds));
  const clamp = (value, minimum, maximum) => Math.min(maximum, Math.max(minimum, value));

  const weakestBadge = (item) => {
    if(!item || !item.rate || !item.floor) return '';
    if (item.isWeakest && item.delta < 0) {
      return '<span class="ee-funnel__weakest-badge red">Metric on fire</span>';
    }else if (item.isWeakest && Math.round(item.rate * 100 ) < Math.round(item.floor * 100) ) {
      return '<span class="ee-funnel__weakest-badge beige">Metric to address</span>';
    }
    
    if (item.delta < 0 || Math.round(item.rate * 100 ) < Math.round(item.floor * 100) ) {
      return '<span class="ee-funnel__weakest-badge beige">Metric to address</span>';
    }

    return '';
  };

  class FunnelDiagnostic {
    constructor(root) {
      this.root = root;
      this.config = JSON.parse(root.querySelector('[data-ee-funnel-config]').textContent);
      this.segments = allSegments.filter((segment) => this.config.showCountry || segment.breakdownKey !== 'country');
      this.accessToken = null;
      this.tokenClient = null;
      this.overall = null;
      this.breakdowns = {};
      this.segmentFailures = [];
      this.abortController = null;
      this.requestVersion = 0;
      this.sessionState = null;
      this.reportCache = null;
      this.selectedMetricKey = null;
      this.demoMode = new URLSearchParams(window.location.search).get('ee_funnel_preview') === '1' ||
        (Boolean(window.Shopify && window.Shopify.designMode) && !this.config.clientId);
      this.cacheElements();
      this.bindEvents();
      this.setDefaultDates();
    }

    readSession() {
      const session = this.sessionState;
      if (!session) return null;
      if (!session.token || !Number.isFinite(session.expiresAt) || Date.now() > session.expiresAt - 120000) {
        this.sessionState = null;
        return null;
      }
      return session;
    }

    writeSession(patch) {
      this.sessionState = { ...(this.sessionState || {}), ...patch };
    }

    clearSession() {
      this.sessionState = null;
      this.reportCache = null;
    }

    reportCacheKey(propertyId) {
      return [propertyId, this.dates.startDate, this.dates.endDate, this.config.showCountry ? 'c1' : 'c0'].join('|');
    }

    readReportCache(cacheKey) {
      const cache = this.reportCache;
      if (!cache) return null;
      const expired = !Number.isFinite(cache.savedAt) || Date.now() - cache.savedAt > REPORT_CACHE_TTL;
      if (cache.cacheKey !== cacheKey || expired || !Array.isArray(cache.overallPairs) || !Array.isArray(cache.segmentResults)) {
        return null;
      }
      return cache;
    }

    writeReportCache(cacheKey, overallPairs, segmentResults) {
      this.reportCache = {
        cacheKey,
        savedAt: Date.now(),
        overallPairs,
        segmentResults: segmentResults.map(({ segment, pair }) => ({ segmentId: segment.id, pair }))
      };
    }

    rehydrateSegmentResults(stored) {
      const byId = new Map(this.segments.map((segment) => [segment.id, segment]));
      return stored
        .filter((entry) => byId.has(entry.segmentId) && entry.pair)
        .map((entry) => ({ segment: byId.get(entry.segmentId), pair: entry.pair }));
    }

    cacheElements() {
      this.connectScreen = this.root.querySelector('[data-connect-screen]');
      this.connectButtons = this.root.querySelectorAll('[data-connect-button]');
      this.connectMessage = this.root.querySelector('[data-connect-message]');
      this.contextBar = this.root.querySelector('[data-context-bar]');
      this.propertySelect = this.root.querySelector('[data-property-select]');
      this.startDate = this.root.querySelector('[data-start-date]');
      this.endDate = this.root.querySelector('[data-end-date]');
      this.refreshButton = this.root.querySelector('[data-refresh-report]');
      this.disconnectButton = this.root.querySelector('[data-disconnect]');
      this.report = this.root.querySelector('[data-report]');
      this.reportStatus = this.root.querySelector('[data-report-status]');
      this.statusTitle = this.root.querySelector('[data-status-title]');
      this.statusDetail = this.root.querySelector('[data-status-detail]');
      this.overview = this.root.querySelector('[data-overview]');
      this.metricGrid = this.root.querySelector('[data-metric-grid]');
      this.radarChart = this.root.querySelector('[data-radar-chart]');
      this.firePanel = this.root.querySelector('[data-fire-panel]');
      this.overviewToggle = this.root.querySelector('[data-toggle-overview]');
      this.segmentProgress = this.root.querySelector('[data-segment-progress]');
      this.progressBar = this.root.querySelector('[data-progress-bar]');
      this.progressCopy = this.root.querySelector('[data-progress-copy]');
      this.breakdownList = this.root.querySelector('[data-breakdown-list]');
    }

    bindEvents() {
      this.connectButtons.forEach((button) => {
        button.addEventListener('click', () => this.connect());
      });
      this.propertySelect.addEventListener('change', () => this.loadReport());
      this.startDate.addEventListener('change', () => this.loadReport());
      this.endDate.addEventListener('change', () => this.loadReport());
      this.refreshButton.addEventListener('click', () => this.loadReport(true));
      this.disconnectButton.addEventListener('click', () => this.disconnect());
      this.overviewToggle.addEventListener('click', () => this.toggleOverview());
      this.metricGrid.addEventListener('click', (event) => this.onMetricCardActivate(event));
      this.metricGrid.addEventListener('keydown', (event) => this.onMetricCardActivate(event));
    }

    setDefaultDates() {
      const end = new Date();
      end.setUTCDate(end.getUTCDate() - 1);
      const start = new Date(end);
      start.setUTCDate(start.getUTCDate() - 29);
      const maxDate = end.toISOString().slice(0, 10);
      this.startDate.max = maxDate;
      this.endDate.max = maxDate;
      this.startDate.value = start.toISOString().slice(0, 10);
      this.endDate.value = maxDate;
      const session = this.demoMode ? null : this.readSession();
      if (session && session.startDate && session.endDate) {
        this.startDate.value = session.startDate;
        this.endDate.value = session.endDate;
      }
      this.syncDates();
    }

    syncDates() {
      this.dates = {
        startDate: this.startDate.value,
        endDate: this.endDate.value
      };
    }

    setConnectButtonsDisabled(disabled) {
      this.connectButtons.forEach((button) => {
        button.disabled = disabled;
      });
    }

    async connect() {
      this.connectMessage.textContent = '';
      if (this.demoMode) {
        this.showDemo();
        return;
      }
      if (!this.config.clientId) {
        this.connectMessage.textContent = 'Add the Google OAuth client ID in this section’s theme settings first.';
        return;
      }
      this.setConnectButtonsDisabled(true);
      try {
        await this.waitForGoogleIdentity();
        if (!this.tokenClient) {
          this.tokenClient = window.google.accounts.oauth2.initTokenClient({
            client_id: this.config.clientId,
            scope: ANALYTICS_SCOPE,
            callback: (response) => this.handleToken(response),
            error_callback: () => this.handleConnectError('Google authorization was cancelled or blocked.')
          });
        }
        this.tokenClient.requestAccessToken({ prompt: this.accessToken ? '' : 'consent' });
      } catch (error) {
        this.handleConnectError(error.message || 'Google authorization could not start.');
      }
    }

    waitForGoogleIdentity() {
      if (window.google && window.google.accounts && window.google.accounts.oauth2) return Promise.resolve();
      return new Promise((resolve, reject) => {
        let attempts = 0;
        const timer = window.setInterval(() => {
          attempts += 1;
          if (window.google && window.google.accounts && window.google.accounts.oauth2) {
            window.clearInterval(timer);
            resolve();
          } else if (attempts >= 50) {
            window.clearInterval(timer);
            reject(new Error('Google Identity Services did not load. Please disable content blockers and try again.'));
          }
        }, 200);
      });
    }

    handleConnectError(message) {
      this.setConnectButtonsDisabled(false);
      this.connectMessage.textContent = message;
    }

    async handleToken(response) {
      if (!response || response.error || !response.access_token) {
        this.handleConnectError(response && response.error_description ? response.error_description : 'Google authorization failed.');
        return;
      }
      this.accessToken = response.access_token;
      this.setConnectButtonsDisabled(false);
      this.writeSession({
        token: response.access_token,
        expiresAt: Date.now() + ((Number(response.expires_in) || 3600) * 1000)
      });
      await this.bootstrapReport();
    }

    async bootstrapReport() {
      try {
        this.showReportShell();
        this.setStatus(
          'Finding GA4 properties…',
          'Reading properties available to this Google account.'
        );
        const properties = await this.fetchProperties();
        if (!properties.length) throw new Error('No GA4 properties are available to this Google account.');
        this.populateProperties(properties);
        await this.loadReport();
      } catch (error) {
        if (error.name === 'AbortError') return;
        this.report.hidden = true;
        this.contextBar.hidden = true;
        this.connectScreen.hidden = false;
        this.handleConnectError(error.message || 'GA4 properties could not be loaded.');
      }
    }

    async fetchProperties() {
      const properties = [];
      let pageToken = '';
      do {
        const url = new URL(ADMIN_API);
        url.searchParams.set('pageSize', '200');
        if (pageToken) url.searchParams.set('pageToken', pageToken);
        const response = await this.googleFetch(url.toString());
        (response.accountSummaries || []).forEach((account) => {
          (account.propertySummaries || []).forEach((property) => {
            properties.push({
              id: String(property.property || '').replace('properties/', ''),
              name: property.displayName || property.property,
              account: account.displayName || ''
            });
          });
        });
        pageToken = response.nextPageToken || '';
      } while (pageToken);
      return properties.filter((property) => property.id).sort((a, b) => a.name.localeCompare(b.name));
    }

    populateProperties(properties) {
      this.propertySelect.innerHTML = '';
      properties.forEach((property) => {
        const option = document.createElement('option');
        option.value = property.id;
        option.textContent = `${property.name} · ${property.id}`;
        this.propertySelect.appendChild(option);
      });
      const session = this.readSession();
      if (session && session.propertyId && properties.some((property) => property.id === session.propertyId)) {
        this.propertySelect.value = session.propertyId;
      }
    }

    showReportShell() {
      this.connectScreen.hidden = true;
      this.contextBar.hidden = false;
      this.report.hidden = false;
    }

    async loadReport(forceRefresh = false) {
      const propertyId = this.propertySelect.value;
      if (!propertyId) return;
      if (!this.startDate.value || !this.endDate.value || this.startDate.value > this.endDate.value) {
        this.setStatus('Check the selected dates', 'The start date must be before the end date.', false);
        return;
      }
      this.syncDates();
      if (!this.demoMode) {
        this.writeSession({ propertyId, startDate: this.dates.startDate, endDate: this.dates.endDate });
      }
      const version = ++this.requestVersion;
      if (this.abortController) this.abortController.abort();
      this.abortController = new AbortController();
      this.overall = null;
      this.selectedMetricKey = null;
      this.breakdowns = {};
      this.segmentFailures = [];
      this.overview.hidden = true;
      this.breakdownList.innerHTML = '';
      this.segmentProgress.hidden = true;
      this.setControlsDisabled(true);
      const cacheKey = this.reportCacheKey(propertyId);
      const cached = !forceRefresh && !this.demoMode ? this.readReportCache(cacheKey) : null;
      try {
        this.setStatus(
          cached ? 'Loading the report from memory…' : 'Reading headline funnel metrics…',
          cached ? 'Use the refresh icon to re-query Google Analytics.' : 'Running four closed, two-step active-user funnels.'
        );
        const overallPairs = cached
          ? cached.overallPairs
          : this.demoMode
            ? await this.demoOverallPairs()
            : await this.loadOverallPairs(propertyId, version);
        if (version !== this.requestVersion) return;
        this.overall = this.scorePairs(overallPairs);
        this.renderOverview();
        this.overview.hidden = false;

        if (this.overall.every((item) => item.rate === null)) {
          this.segmentProgress.hidden = true;
          this.setStatus(
            'No qualifying funnel data was found',
            'Try a different date range or confirm that GA4 is receiving the ecommerce events used by this report.',
            false
          );
          return;
        }

        if (!cached) {
          this.segmentProgress.hidden = false;
          this.setStatus('Headline report ready', 'Segment tables are loading in the background.');
        }

        const segmentResults = cached
          ? this.rehydrateSegmentResults(cached.segmentResults)
          : this.demoMode
            ? await this.demoSegmentResults()
            : await this.loadSegmentsInBatches(propertyId, version);
        if (version !== this.requestVersion) return;
        this.breakdowns = this.buildBreakdowns(segmentResults);
        this.renderBreakdowns();
        this.renderFirePanel();
        this.segmentProgress.hidden = true;
        if (!cached && !this.demoMode && !this.segmentFailures.length) {
          this.writeReportCache(cacheKey, overallPairs, segmentResults);
        }
        if (this.segmentFailures.length) {
          const queryLabel = this.segmentFailures.length === 1 ? 'query' : 'queries';
          this.setStatus(
            'Report loaded with some unavailable segments',
            `${this.segmentFailures.length} ${queryLabel} remained throttled after automatic retries. Wait a minute, then refresh the report.`,
            false
          );
        } else {
          this.reportStatus.hidden = true;
        }
      } catch (error) {
        if (error.name === 'AbortError') return;
        this.setStatus('The GA4 report could not be loaded', error.message || 'Please reconnect and try again.', false);
      } finally {
        if (version === this.requestVersion) this.setControlsDisabled(false);
      }
    }

    setControlsDisabled(disabled) {
      this.propertySelect.disabled = disabled;
      this.startDate.disabled = disabled;
      this.endDate.disabled = disabled;
      this.refreshButton.disabled = disabled;
    }

    dateRange() {
      return { ...this.dates };
    }

    funnelBody(transition, dimensionFilter) {
      return {
        dateRanges: [this.dateRange()],
        funnel: {
          isOpenFunnel: false,
          steps: [
            { name: transition.fromName, filterExpression: transition.fromFilter },
            { name: transition.toName, filterExpression: transition.toFilter }
          ]
        },
        ...(dimensionFilter ? { dimensionFilter } : {}),
        returnPropertyQuota: true
      };
    }

    async runFunnelPair(propertyId, transition, dimensionFilter) {
      const response = await this.googleFetch(`${DATA_API}/${propertyId}:runFunnelReport`, {
        method: 'POST',
        body: JSON.stringify(this.funnelBody(transition, dimensionFilter))
      });
      return this.parsePair(response, transition);
    }

    async loadOverallPairs(propertyId, version) {
      const pairs = [];
      for (const transition of transitions) {
        if (version !== this.requestVersion) return [];
        pairs.push(await this.runFunnelPair(propertyId, transition));
      }
      return pairs;
    }

    parsePair(response, transition) {
      const table = response.funnelTable || {};
      const dimensions = (table.dimensionHeaders || []).map((item) => item.name || '');
      const metrics = (table.metricHeaders || []).map((item) => item.name || '');
      const activeUsersIndex = metrics.indexOf('activeUsers');
      let fromUsers = 0;
      let toUsers = 0;
      (table.rows || []).forEach((row) => {
        const record = {};
        dimensions.forEach((name, index) => { record[name] = row.dimensionValues?.[index]?.value || ''; });
        const stepName = record.funnelStepName || '';
        const users = Number(row.metricValues?.[activeUsersIndex]?.value || 0);
        if (stepName.endsWith(transition.fromName)) fromUsers = users;
        if (stepName.endsWith(transition.toName)) toUsers = users;
      });
      return { key: transition.key, fromUsers, toUsers };
    }

    async googleFetch(url, options = {}) {
      const signal = this.abortController?.signal;
      const maxRetries = 3;
      for (let attempt = 0; attempt <= maxRetries; attempt += 1) {
        if (signal?.aborted) throw new DOMException('The request was aborted.', 'AbortError');
        const response = await window.fetch(url, {
          ...options,
          signal,
          headers: {
            Authorization: `Bearer ${this.accessToken}`,
            'Content-Type': 'application/json',
            ...(options.headers || {})
          }
        });
        if (response.ok) return response.json();

        let detail = '';
        try {
          const payload = await response.json();
          detail = payload.error?.message || payload.error_description || '';
        } catch (_error) {
          detail = '';
        }

        if (response.status === 429 && attempt < maxRetries) {
          const retryAfter = response.headers.get('Retry-After');
          const retryAfterSeconds = Number.parseFloat(retryAfter || '');
          const retryAfterDate = retryAfter && !Number.isFinite(retryAfterSeconds) ? Date.parse(retryAfter) : NaN;
          const fallbackDelay = (1000 * (2 ** attempt)) + Math.floor(Math.random() * 250);
          const retryDelay = Number.isFinite(retryAfterSeconds)
            ? Math.max(1000, retryAfterSeconds * 1000)
            : Number.isFinite(retryAfterDate)
              ? Math.max(1000, retryAfterDate - Date.now())
              : fallbackDelay;
          this.setStatus(
            'Google Analytics is temporarily throttling requests…',
            `Retrying automatically in ${Math.ceil(retryDelay / 1000)} seconds.`
          );
          await delay(retryDelay);
          continue;
        }

        if (response.status === 401) {
          this.accessToken = null;
          this.sessionState = null;
        }
        const error = new Error(detail || `Google Analytics returned ${response.status}.`);
        error.status = response.status;
        throw error;
      }
      throw new Error('Google Analytics could not complete the request.');
    }

    async loadSegmentsInBatches(propertyId, version) {
      const tasks = this.segments.flatMap((segment) => transitions.map((transition) => ({ segment, transition })));
      const output = [];
      const batchSize = 3;
      for (let index = 0; index < tasks.length; index += batchSize) {
        if (version !== this.requestVersion) return [];
        const batch = tasks.slice(index, index + batchSize);
        const startedAt = Date.now();
        const results = await Promise.all(batch.map(async ({ segment, transition }) => {
          try {
            const pair = await this.runFunnelPair(propertyId, transition, segment.filter);
            return { segment, pair };
          } catch (error) {
            if (error.name === 'AbortError') throw error;
            this.segmentFailures.push({ segmentId: segment.id, transitionKey: transition.key, message: error.message });
            return { segment, pair: { key: transition.key, fromUsers: null, toUsers: null, unavailable: true } };
          }
        }));
        output.push(...results);
        const completed = Math.min(index + batch.length, tasks.length);
        const percent = Math.round((completed / tasks.length) * 100);
        this.progressBar.style.width = `${percent}%`;
        this.progressCopy.textContent = `Building segment analysis · ${completed} of ${tasks.length} queries`;
        if (completed < tasks.length) {
          const remainingWindow = Math.max(0, 1100 - (Date.now() - startedAt));
          if (remainingWindow) await delay(remainingWindow);
        }
      }
      return output;
    }

    scorePairs(pairs) {
      const byKey = Object.fromEntries(pairs.map((pair) => [pair.key, pair]));
      const scored = transitions.map((definition) => {
        const pair = byKey[definition.key] || { fromUsers: null, toUsers: null, unavailable: true };
        const target = this.config.targets[definition.key];
        const rate = pair.fromUsers > 0 ? pair.toUsers / pair.fromUsers : null;
        const delta = rate === null ? null : Math.round(((rate - target.floor) / target.floor) * 100);
        let status = 'unscorable';
        if (rate !== null) status = rate < target.floor ? 'critical' : rate < target.ceiling ? 'meets' : 'strong';
        const midpoint = (target.floor + target.ceiling) / 2;
        let tableStatus = 'unscorable';
        if (rate !== null) tableStatus = rate >= midpoint ? 'meets' : rate >= midpoint * 0.7 ? 'below' : 'critical';
        return { ...definition, ...pair, ...target, rate, delta, status, tableStatus, isWeakest: false };
      });
      const scorable = scored.filter((item) => item.delta !== null).sort((a, b) => a.delta - b.delta);
      const weakestKey = scorable.length ? scorable[0].key : null;
      return scored.map((item) => ({ ...item, isWeakest: item.key === weakestKey }));
    }

    buildBreakdowns(results) {
      const groups = {};
      breakdownDefinitions.forEach((definition) => {
        if (!this.config.showCountry && definition.key === 'country') return;
        groups[definition.key] = { ...definition, rows: [] };
      });
      this.segments.forEach((segment) => {
        const pairs = results.filter((result) => result.segment.id === segment.id).map((result) => result.pair);
        const transitionsForSegment = this.scorePairs(pairs);
        const landingPair = pairs.find((pair) => pair.key === 'landing_to_product');
        const allLanding = this.overall.find((item) => item.key === 'landing_to_product');
        const percentOfEntrants = landingPair && Number.isFinite(landingPair.fromUsers) && allLanding && allLanding.fromUsers > 0
          ? landingPair.fromUsers / allLanding.fromUsers
          : null;
        groups[segment.breakdownKey].rows.push({
          id: segment.id,
          label: segment.label,
          percentOfEntrants,
          transitions: Object.fromEntries(transitionsForSegment.map((item) => [item.key, item]))
        });
      });
      return groups;
    }

    renderOverview() {
      const eventDescription = {
        'landing_to_product': 'How successfully can users advance from landing on your store to actually viewing a product?',
        'product_to_cart': 'How well does your product page convince customers to add an item to their cart?',
        'cart_to_checkout': 'How efficiently can users move from add to cart into the checkout process?',
        'checkout_to_purchase': 'How well can users who start the checkout process continue on to purchase?' 
      };

      const weakest = this.overall.find((item) => item.isWeakest);
      this.selectedMetricKey = weakest?.key || this.overall[0]?.key || null;

      this.metricGrid.innerHTML = this.overall.map((item) => `
       
        <article class="ee-funnel__metric-card"  data-status="${item.status}" data-weakest="${item.isWeakest && item.delta < 0}" data-key="${item.key}" data-selected="${item.key === this.selectedMetricKey}" role="button" tabindex="0" aria-pressed="${item.key === this.selectedMetricKey}">
          ${weakestBadge(item)}
          <div class="ee-funnel__metric-content">
          <h2 class="ee-funnel__metric-label">${item.label}</h2>
          <div class="ee-funnel__metric-rate" title="${item.rate === null ? 'Not enough data: denominator is zero.' : ''}">${formatPercent(item.rate)}</div>
          <span class="ee-funnel__metric-target">Target ${(item.floor * 100).toFixed(0)}–${(item.ceiling * 100).toFixed(0)}%</span>
           <div class="ee-funnel__metric-delta" data-status="${item.status}">
            ${formatSigned(item.delta)}
            <small>vs target</small>
            </div>
          </div>



            <div class="ee-funnel__metric-hover-content">
              <h2 class="ee-funnel__metric-label">${item.label}</h2>
              <p>${eventDescription[item.key]}</p>
              <span class="ee-funnel__metric-target">Target Range: ${(item.floor * 100).toFixed(0)}–${(item.ceiling * 100).toFixed(0)}%</span>
            </div>
       
        </article>
      `).join('');
      this.renderRadar();
      this.renderFirePanel();
    }

    renderRadar() {
      const scores = this.overall.map((item) => item.rate !== null && Number.isFinite(item.floor) && item.floor > 0
        ? Math.min(100, Math.round((item.rate / item.floor) * 100))
        : null);
      const center = 130;
      const radius = 116;
      const axes = { top: -90, right: 0, bottom: 90, left: 180 };
      const point = (score, angle) => {
        const radians = angle * Math.PI / 180;
        const distance = clamp(score, 0, 100) / 100 * radius;
        return `${center + Math.cos(radians) * distance},${center + Math.sin(radians) * distance}`;
      };
      const ring = (value) => [axes.top, axes.right, axes.bottom, axes.left].map((angle) => point(value, angle)).join(' ');
      const quadrants = [
        { corner: 'top-left', angles: [axes.left, axes.top], from: '#ff291b', to: '#ff7100', x1: 0, y1: 0, x2: 1, y2: 1 },
        { corner: 'top-right', angles: [axes.top, axes.right], from: '#ff7100', to: '#ff9f00', x1: 1, y1: 0, x2: 0, y2: 1 },
        { corner: 'bottom-left', angles: [axes.bottom, axes.left], from: '#ffca1f', to: '#ffbd00', x1: 0, y1: 1, x2: 1, y2: 0 },
        { corner: 'bottom-right', angles: [axes.right, axes.bottom], from: '#ff9f00', to: '#ffbd00', x1: 1, y1: 1, x2: 0, y2: 0 }
      ];
      const gradientId = (index) => `${this.root.id}-radar-q${index}`;
      const clipId = `${this.root.id}-radar-clip`;
      const trianglePoints = quadrants.map((quadrant, index) => scores[index]
        ? `${center},${center} ${point(scores[index], quadrant.angles[0])} ${point(scores[index], quadrant.angles[1])}`
        : '');
      const triangles = trianglePoints.map((points, index) => points
        ? `<polygon points="${points}" fill="url(#${gradientId(index)})"></polygon>`
        : '').join('');
      this.radarChart.innerHTML = `
        <div class="ee-funnel__radar-quad">
          ${this.overall.map((item, index) => `
            <div class="ee-funnel__radar-corner" data-corner="${quadrants[index].corner}" data-status="${item.status}">
              <span>${item.fromName} → ${item.toName}</span>
              <strong>${formatPercent(item.rate)}</strong>
              <small>${scores[index] === null ? 'Not enough data' : `${scores[index]}% of target`}</small>
            </div>
          `).join('')}
          <svg viewBox="0 0 260 260" role="img" aria-label="Funnel health per transition, normalized against each target floor">
            <defs>
              ${quadrants.map((quadrant, index) => `
                <linearGradient id="${gradientId(index)}" x1="${quadrant.x1}" y1="${quadrant.y1}" x2="${quadrant.x2}" y2="${quadrant.y2}">
                  <stop offset="0" stop-color="${quadrant.from}"></stop>
                  <stop offset="1" stop-color="${quadrant.to}"></stop>
                </linearGradient>
              `).join('')}
              <clipPath id="${clipId}">
                ${trianglePoints.map((points) => points ? `<polygon points="${points}"></polygon>` : '').join('')}
              </clipPath>
            </defs>
            <polygon points="${ring(100)}" fill="#fff" stroke="#dcdcdc" stroke-width="1"></polygon>
            ${[33, 67].map((value) => `<polygon points="${ring(value)}" fill="none" stroke="#e2e0e0" stroke-width="1.5" stroke-dasharray="5 5"></polygon>`).join('')}
            ${triangles}
            <g clip-path="url(#${clipId})">
              ${[33, 67].map((value) => `<polygon points="${ring(value)}" fill="none" stroke="#fff" stroke-width="1.5" stroke-dasharray="5 5"></polygon>`).join('')}
              <line x1="${center}" y1="${center - radius}" x2="${center}" y2="${center + radius}" stroke="#fff" stroke-width="1.5"></line>
              <line x1="${center - radius}" y1="${center}" x2="${center + radius}" y2="${center}" stroke="#fff" stroke-width="1.5"></line>
            </g>
          </svg>
        </div>
      `;
    }

    segmentStatsForTransition(key) {
      const ranked = [];
      let belowFloorCount = 0;
      let totalCount = 0;

      for (const breakdown of Object.values(this.breakdowns || {})) {
        for (const row of breakdown.rows) {
          const result = row.transitions[key];

          if (!result || result.rate === null) continue;

          totalCount += 1;

          if (result.rate < result.floor) belowFloorCount += 1;

          ranked.push({ label: row.label, rate: result.rate, delta: result.delta });
        }
      }

      ranked.sort((a, b) => a.delta - b.delta);

      return { ranked, belowFloorCount, totalCount };
    }

    firePanelEyebrow(item) {
      if (item.status === 'unscorable') return 'Not enough data';
      if (item.isWeakest && item.status === 'critical') return 'Metric on fire';
      if (item.isWeakest) return 'Weakest metric';
      if (item.status === 'critical') return 'Below target floor';
      if (item.status === 'strong') return 'Above target ceiling';

      return 'Within target range';
    }

    firePanelCard(item, index = 1) {
      const { ranked, belowFloorCount, totalCount } = this.segmentStatsForTransition(item.key);
      const opportunityCopy = item.isWeakest
        ? 'This transition has the lowest conversion rate of the four funnel steps. Start here when prioritizing funnel improvements.'
        : 'How this step compares to its target floor, with the segments that underperform it most.';
      const segmentCopy = totalCount
        ? `${belowFloorCount} of ${totalCount} scorable segments are below this step’s target floor.`
        : 'Segment analysis is still loading.';
      const chips = ranked.length
        ? `
          <span class="ee-funnel__segment-label hidden">Lowest-performing segments</span>
          <div class="ee-funnel__chips hidden">${ranked.slice(0, 6).map((chip) => `<span>${chip.label} · ${formatPercent(chip.rate)}</span>`).join('')}</div>
        `
        : '';
        let definition = '';
        let overview = '';
        if(item.key === 'landing_to_product') {
          overview = '94.6% of brands have at least one type of product discovery issue. Traffic driven directly to product pages skews data. When filtered out, Homepage > PDP and PLP > PDP rates are far below 50%.';
          definition = '<strong>Definition:</strong> Users trigger an "view_item" event.';
        } else if(item.key === 'product_to_cart') {
          overview = 'Product View → Add to Cart is vital to strong funnel performance. A narrow minority of stores (49.3%), fail to meet target rates. Bringing this metric up to par is crucial for sites looking to expand paid traffic.';
          definition = '<strong>Definition:</strong> Users trigger an "view_item" AND "add_to_cart" event.';
        } else if(item.key === 'cart_to_checkout') {
          overview = '39.6% of brands fail to meet target rates for Cart → Checkout. Most Shopify stores do "well enough" on this metric. Some industries, such as apparel, tend to struggle more than others.';
          definition = '<strong>Definition:</strong> Users trigger an "add_to_cart" AND "begin_checkout" event.';
        } else if(item.key === 'checkout_to_purchase') {
          overview = '32% of stores fail to meet the Checkout → Purchase target rates. However, most stores fall just shy of the target range. Unless a brand is selling across multiple regions globally, there tends to be minimal room for improvement in most Shopify stores.';
          definition = '<strong>Definition:</strong> Users trigger an "begin_checkout" AND "purchase" event.';
        }
      return `
        <article class="ee-funnel__fire-panel" data-key="${item.key}" data-status="${item.status}" data-weakest="${item.isWeakest}" ${item.key === this.selectedMetricKey ? '' : 'hidden'}>
          <span class="ee-funnel__fire-panel-index">METRIC ${index + 1}</span>

          <span class="hidden ee-funnel__fire-eyebrow">${this.firePanelEyebrow(item)}</span>
          <span class="hidden ee-funnel__fire-delta">${formatSigned(item.delta)} to target floor</span>
          <p class="hidden ee-funnel__fire-copy">${segmentCopy}</p>
          <h2 class="ee-funnel__fire-heading">${item.label}</h2>
          <span class="ee-funnel__fire-definition"> ${definition}</span>
          <p class="ee-funnel__fire-overview">
          <strong>Overview:</strong>
          <span class="ee-funnel__fire-copy">${overview}</span>
          </p>
          <div class="ee-funnel__top-line">
          <span class="ee-funnel__target-rate">| Target Rate: ${(item.floor * 100).toFixed(0)}%–${(item.ceiling * 100).toFixed(0)}% |</span>
          <p class="ee-funnel__average-rate">Average Rate: <strong>${formatPercent(item.rate)}</strong></p>
          </div>
          ${chips}
        </article>
      `;
    }

    onMetricCardActivate(event) {
      const isKeyboard = event.type === 'keydown';

      if (isKeyboard && event.key !== 'Enter' && event.key !== ' ') return;

      const card = event.target.closest('.ee-funnel__metric-card');

      if (!card || !this.metricGrid.contains(card) || !card.dataset.key) return;
      if (isKeyboard) event.preventDefault();

      this.selectMetric(card.dataset.key);
    }

    selectMetric(key) {
      if (!key) return;

      this.selectedMetricKey = key;

      for (const card of this.metricGrid.querySelectorAll('.ee-funnel__metric-card')) {
        const selected = card.dataset.key === key;
        card.dataset.selected = String(selected);
        card.setAttribute('aria-pressed', String(selected));
      }

      this.syncFirePanelVisibility();
    }

    syncFirePanelVisibility() {
      for (const panel of this.firePanel.querySelectorAll('.ee-funnel__fire-panel')) {
        panel.hidden = panel.dataset.key !== this.selectedMetricKey;
      }
    }

    renderFirePanel() {
      if (!this.overall || !this.overall.length) {
        this.firePanel.innerHTML = '';
        return;
      }

      if (!this.selectedMetricKey || !this.overall.some((item) => item.key === this.selectedMetricKey)) {
        const weakest = this.overall.find((item) => item.isWeakest);
        this.selectedMetricKey = weakest?.key || this.overall[0].key;
      }

      this.firePanel.innerHTML = this.overall.map((item, index) => this.firePanelCard(item, index)).join('');
      this.selectMetric(this.selectedMetricKey);
    }

    renderBreakdowns() {
      this.breakdownList.innerHTML = '';
      breakdownDefinitions.forEach((definition) => {
        const breakdown = this.breakdowns[definition.key];
        if (!breakdown || !breakdown.rows.length) return;
        const section = document.createElement('section');
        section.className = 'ee-funnel__breakdown';
        section.innerHTML = `
          <header class="ee-funnel__breakdown-header">
            <h2>${definition.label}</h2>
            <div class="ee-funnel__tabs" role="group" aria-label="View ${definition.label} as">
              <button type="button" data-view="table" aria-pressed="true">Table form</button>
              <button type="button" data-view="graph" aria-pressed="false">Graph form</button>
            </div>
          </header>
          <div data-breakdown-body></div>
        `;
        const body = section.querySelector('[data-breakdown-body]');
        let selectedId = breakdown.rows[0].id;
        let activeView = 'table';
        const draw = () => {
          if (activeView === 'table') this.renderTable(body, breakdown);
          else this.renderGraph(body, breakdown, selectedId, (id) => { selectedId = id; draw(); });
          section.classList.toggle('ee-funnel__breakdown--graph', activeView === 'graph');
          section.querySelectorAll('[data-view]').forEach((button) => {
            button.setAttribute('aria-pressed', String(button.dataset.view === activeView));
          });
        };
        section.querySelectorAll('[data-view]').forEach((button) => {
          button.addEventListener('click', () => { activeView = button.dataset.view; draw(); });
        });
        draw();
        this.breakdownList.appendChild(section);
      });
    }

    renderTable(container, breakdown) {
      container.innerHTML = `
        <div class="ee-funnel__table-scroll">
          <table class="ee-funnel__table">
            <thead>
              <tr>
                <th scope="col">${breakdown.label.replace(/^By /, '')}</th>
                <th scope="col">% Users</th>
                ${transitions.map((item) => `<th scope="col">${item.label}</th>`).join('')}
              </tr>
            </thead>
            <tbody>
              ${breakdown.rows.map((row) => `
                <tr>
                  <th scope="row">${row.label}</th>
                  <td>${formatPercent(row.percentOfEntrants)}</td>
                  ${transitions.map((definition) => {
                    const result = row.transitions[definition.key];
                    return `<td data-status="${result.tableStatus}" title="${result.rate === null ? 'Not enough data: denominator is zero.' : ''}">${formatPercent(result.rate)}</td>`;
                  }).join('')}
                </tr>
              `).join('')}
            </tbody>
          </table>
        </div>
      `;
    }

    renderGraph(container, breakdown, selectedId, onSelect) {
      const selected = breakdown.rows.find((row) => row.id === selectedId) || breakdown.rows[0];
      if (!selected) {
        container.innerHTML = '<p class="ee-funnel__empty">No breakdown data is available.</p>';
        return;
      }
      container.innerHTML = `
        <div class="ee-funnel__graph-layout">
          <div class="ee-funnel__graph" role="img" aria-label="${breakdown.label} chart for ${selected.label}">
            <div class="ee-funnel__y-axis"><span>100%</span><span>75%</span><span>50%</span><span>25%</span><span>0%</span></div>
            <div class="ee-funnel__bars">
              ${transitions.map((definition) => {
                const result = selected.transitions[definition.key];
                const actual = result.rate === null ? 0 : clamp(result.rate * 100, 0, 100);
                const target = clamp(result.floor * 100, 0, 100);
                const targetMet = result.rate !== null && actual >= target;
                const targetLabelBottom = targetMet ? actual : target;
                const targetLabel = targetMet ? 'Target Completed' : `Target ${target.toFixed(0)}%`;
                return `
                  <div class="ee-funnel__bar-column">
                    <div class="ee-funnel__bar-plot">
                      <div class="ee-funnel__target-line" data-met="${targetMet}" style="bottom:${targetLabelBottom}%"><span>${targetLabel}</span></div>
                      <div class="ee-funnel__bar" data-status="${result.tableStatus}" style="height:${result.rate === null ? 1 : actual}%" title="${result.rate === null ? 'Not enough data: denominator is zero.' : ''}">
                        <strong>${formatPercent(result.rate)}</strong>
                      </div>
                    </div>
                    <div class="ee-funnel__bar-label">${definition.shortLabel}</div>
                  </div>
                `;
              }).join('')}
            </div>
          </div>
          <aside class="ee-funnel__graph-aside">
            <span>Users</span>
            <strong>${formatPercent(selected.percentOfEntrants)}</strong>
            <div class="ee-funnel__segment-buttons">
              ${breakdown.rows.map((row) => `<button type="button" data-segment-id="${row.id}" aria-pressed="${row.id === selected.id}">${row.label}</button>`).join('')}
            </div>
          </aside>
        </div>
      `;
      container.querySelectorAll('[data-segment-id]').forEach((button) => {
        button.addEventListener('click', () => onSelect(button.dataset.segmentId));
      });
    }

    toggleOverview() {
      const expanded = this.overviewToggle.getAttribute('aria-expanded') === 'true';
      this.overviewToggle.setAttribute('aria-expanded', String(!expanded));
      this.overview.hidden = expanded;
      this.overviewToggle.querySelector('[data-toggle-label]').textContent = `${expanded ? 'Open' : 'Close'} schematics`;
    }

    setStatus(title, detail, loading = true) {
      this.reportStatus.hidden = false;
      this.reportStatus.classList.toggle('ee-funnel__status--center', this.overview.hidden);
      this.statusTitle.textContent = title;
      this.statusDetail.textContent = detail || '';
      const spinner = this.reportStatus.querySelector('.ee-funnel__spinner');
      spinner.hidden = !loading;
    }

    disconnect() {
      ++this.requestVersion;
      if (this.abortController) this.abortController.abort();
      this.abortController = null;
      this.clearSession();
      const token = this.accessToken;
      this.accessToken = null;
      if (token && token !== 'demo' && window.google?.accounts?.oauth2) {
        window.google.accounts.oauth2.revoke(token, () => {});
      }
      this.report.hidden = true;
      this.contextBar.hidden = true;
      this.connectScreen.hidden = false;
      this.connectMessage.textContent = '';
      this.setConnectButtonsDisabled(false);
    }

    showDemo() {
      this.accessToken = 'demo';
      this.populateProperties([{ id: '398214075', name: 'Northfield Supply Co.' }]);
      this.showReportShell();
      this.loadReport();
    }

    async demoOverallPairs() {
      await delay(350);
      const rates = [0.614, 0.158, 0.523, 0.571];
      const from = [10000, 6140, 970, 507];
      return transitions.map((transition, index) => ({
        key: transition.key,
        fromUsers: from[index],
        toUsers: Math.round(from[index] * rates[index])
      }));
    }

    async demoSegmentResults() {
      const output = [];
      const visibleSegments = new Map(this.segments.map((segment) => [segment.id, segment]));
      const rows = Object.values(demoRows).flat();
      const total = rows.filter(([id]) => visibleSegments.has(id)).length * transitions.length;
      let completed = 0;
      this.progressBar.style.width = '0%';
      for (const [id, percent, rates] of rows) {
        const segment = visibleSegments.get(id);
        if (!segment) continue;
        rates.forEach((rate, index) => {
          const base = index === 0 ? Math.round(10000 * percent / 100) : 1000;
          output.push({ segment, pair: { key: transitions[index].key, fromUsers: base, toUsers: Math.round(base * rate) } });
          completed += 1;
        });
        this.progressBar.style.width = `${Math.round(completed / total * 100)}%`;
        this.progressCopy.textContent = `Building preview segment analysis · ${completed} of ${total} queries`;
        await delay(35);
      }
      return output;
    }
  }

  /**
   * Smooth-scrolls from the hero fold to the logo strip, offsetting the sticky brand bar.
   * @param {HTMLElement} root
   * @returns {void}
   */
  const bindScrollDown = (root) => {
    const button = root.querySelector('[data-scroll-down]');
    if (!button) return;

    button.addEventListener('click', () => {
      const target = root.querySelector('.ee-funnel__final-cta');
      if (!target) return;

      const header = root.querySelector('.ee-funnel__brand-bar');
      const offset = header ? header.offsetHeight : 0;
      const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const top = target.getBoundingClientRect().top + window.scrollY - offset;

      window.scrollTo({
        top: Math.max(0, top),
        behavior: prefersReducedMotion ? 'auto' : 'smooth'
      });
    });
  };

  const initialize = (scope = document) => {
    scope.querySelectorAll('[data-ee-funnel-diagnostic]').forEach((root) => {
      if (root.dataset.eeInitialized === 'true') return;
      root.dataset.eeInitialized = 'true';
      new FunnelDiagnostic(root);
      bindScrollDown(root);
    });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initialize());
  else initialize();
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
})();
